import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import {
  attachMappedApiError,
  isBlobLikeErrorBody,
  parseHttpErrorBlobBody,
} from '@ewentscompany/ewents-settings';
import { Observable, catchError, from, switchMap, throwError } from 'rxjs';

/**
 * Global API error enrichment — does NOT toast.
 *
 * Attaches `mappedMessage` / `mappedCode` via shared `attachMappedApiError`
 * while keeping `HttpErrorResponse` identity (status, error body, instanceof).
 * Catch sites still call `extractErrorMessage` for inline form / snackbar UX;
 * reading `error.mappedMessage` is optional and equivalent for default options.
 *
 * Blob downloads (`responseType: 'blob'`): Angular stores the JSON error body as
 * a Blob — hydrate it before mapping so VALIDATION_ERROR messages (e.g. EWE-031)
 * surface instead of the generic status-400 copy.
 */
function enrichApiError(error: unknown): Observable<never> {
  if (error instanceof HttpErrorResponse && isBlobLikeErrorBody(error.error)) {
    return from(parseHttpErrorBlobBody(error.error)).pipe(
      switchMap(parsed => {
        const hydrated = new HttpErrorResponse({
          error: parsed,
          headers: error.headers,
          status: error.status,
          statusText: error.statusText,
          url: error.url ?? undefined,
        });
        return throwError(() => attachMappedApiError(hydrated));
      })
    );
  }
  if (error instanceof HttpErrorResponse) {
    return throwError(() => attachMappedApiError(error));
  }
  if (error && typeof error === 'object') {
    return throwError(() => attachMappedApiError(error));
  }
  return throwError(() => error);
}

export const apiErrorInterceptor: HttpInterceptorFn = (req, next) =>
  next(req).pipe(catchError(enrichApiError));
